/* eslint-disable no-console */
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { ContactRoleContext } from '../../../providers/ContactRoleContext';
import { updateContact } from '../../../api/salesforce';
import TextInput from '../../atoms/TextInput/TextInput';
import TitleHeader from '../TitleHeader/TitleHeader';
import Button from '../../atoms/Button/Button';
import Card from '../../atoms/Card/Card';
import SavedInput from '../../atoms/SavedInput/SavedInput';
import { UserContext } from '../../../providers/UserContext';
import './LeadTenantForm.scss';

const LeadTenantForm = ({ copy, fields }) => {
  const { householdData, setHouseholdData } = useContext(ContactRoleContext);
  const { LeadTenant: LeadTenantArray } = householdData;
  const leadTenant = LeadTenantArray[0] || {};

  const fieldsById = useMemo(
    () => fields.reduce((acc, field) => ({
      ...acc,
      [field.id]: field,
    }), {}),
    [fields],
  );

  const getLeadTenantFieldValue = (fieldId) => {
    const leadTenantFields = leadTenant.fields || [];
    const matchedField = leadTenantFields.find((field) => field.id === fieldId);
    return (matchedField && matchedField.value) || '';
  };

  const currentValues = {
    firstName: getLeadTenantFieldValue('firstName'),
    lastName: getLeadTenantFieldValue('lastName'),
    email: getLeadTenantFieldValue('email'),
    phoneNumber: getLeadTenantFieldValue('phoneNumber'),
  };

  const {
    register,
    errors,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: currentValues,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const { contactId } = useContext(UserContext);
  const leadTenantContactId = leadTenant.contactId || contactId;
  const leadTenantOcrId = leadTenant.ocrId;

  const formErrorMessage = (
    (errors.firstName && errors.firstName.message)
    || (errors.lastName && errors.lastName.message)
    || (errors.phoneNumber && errors.phoneNumber.message)
  );

  useEffect(() => {
    reset({
      firstName: currentValues.firstName,
      lastName: currentValues.lastName,
      email: currentValues.email,
      phoneNumber: currentValues.phoneNumber,
    });
  }, [
    currentValues.firstName,
    currentValues.lastName,
    currentValues.email,
    currentValues.phoneNumber,
    reset,
  ]);

  const onEdit = () => {
    setSaveErrorMessage('');
    setIsEditing(true);
  };

  const onCancel = () => {
    setSaveErrorMessage('');
    reset(currentValues);
    setIsEditing(false);
  };

  const onSubmit = async (data) => {
    setIsSaving(true);
    try {
      setSaveErrorMessage('');
      const hasContactChanges = (
        data.firstName !== currentValues.firstName
        || data.lastName !== currentValues.lastName
        || data.phoneNumber !== currentValues.phoneNumber
      );

      if (hasContactChanges && leadTenantContactId && leadTenantOcrId) {
        await updateContact(leadTenantContactId, leadTenantOcrId, {
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
        });
      }

      const updatedFields = Object.entries({
        firstName: data.firstName,
        lastName: data.lastName,
        email: currentValues.email,
        phoneNumber: data.phoneNumber,
      }).map(([id, value]) => ({
        id,
        label: (fieldsById[id] && fieldsById[id].label) || id,
        value,
      }));

      setHouseholdData({
        ...householdData,
        LeadTenant: [
          {
            ...leadTenant,
            fields: updatedFields,
          },
        ],
      });

      reset({
        firstName: data.firstName,
        lastName: data.lastName,
        email: currentValues.email,
        phoneNumber: data.phoneNumber,
      });
      setIsEditing(false);
    } catch (err) {
      setSaveErrorMessage('We could not save your details. Please try again.');
      console.error('Error saving lead tenant details', err);
    } finally {
      setIsSaving(false);
    }
  };

  const onInvalidSubmit = () => {
    setSaveErrorMessage('');
  };

  const leadTenantFields = (leadTenant && leadTenant.fields) || [];
  const leadTenantFieldsById = leadTenantFields.reduce((acc, field) => ({
    ...acc,
    [field.id]: field,
  }), {});
  const displayLeadTenantFields = fields.map((fieldDefinition) => {
    const fieldValue = leadTenantFieldsById[fieldDefinition.id];
    return {
      id: fieldDefinition.id,
      label: (fieldValue && fieldValue.label) || fieldDefinition.label,
      value: (fieldValue && fieldValue.value) || '',
    };
  });

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)} noValidate>
      <Card>
        <TitleHeader category={copy.heading} />

        {isEditing ? (
          <>
            <div className="leadTenantForm row" data-testid="editingLeadTenantForm">
              <TextInput
                label={fieldsById.firstName && fieldsById.firstName.label}
                type={fieldsById.firstName && fieldsById.firstName.type}
                id="firstName"
                required
                testId="leadTenantFirstName"
                className="col-lg-6"
                register={register}
                validation={fieldsById.firstName && fieldsById.firstName.validation}
                hasError={errors.firstName}
                showErrorMessage={false}
                defaultValue={currentValues.firstName}
              />
              <TextInput
                label={fieldsById.lastName && fieldsById.lastName.label}
                type={fieldsById.lastName && fieldsById.lastName.type}
                id="lastName"
                required
                testId="leadTenantLastName"
                className="col-lg-6"
                register={register}
                validation={fieldsById.lastName && fieldsById.lastName.validation}
                hasError={errors.lastName}
                showErrorMessage={false}
                defaultValue={currentValues.lastName}
              />
              <TextInput
                label={fieldsById.email && fieldsById.email.label}
                type={fieldsById.email && fieldsById.email.type}
                id="email"
                required
                testId="leadTenantEmail"
                className="col-lg-6"
                register={register}
                validation={fieldsById.email && fieldsById.email.validation}
                hasError={errors.email}
                defaultValue={currentValues.email}
                disabled
              />
              <TextInput
                label={fieldsById.phoneNumber && fieldsById.phoneNumber.label}
                type={fieldsById.phoneNumber && fieldsById.phoneNumber.type}
                id="phoneNumber"
                required
                testId="leadTenantPhone"
                className="col-lg-6"
                register={register}
                validation={fieldsById.phoneNumber && fieldsById.phoneNumber.validation}
                hasError={errors.phoneNumber}
                defaultValue={currentValues.phoneNumber}
              />
            </div>

            {formErrorMessage && (
              <span className="textInput__error-message" data-testid="test-error">
                {formErrorMessage}
              </span>
            )}
            {saveErrorMessage && (
              <span className="textInput__error-message" data-testid="saveErrorMessage">
                {saveErrorMessage}
              </span>
            )}

            <div className="leadTenantForm__buttonContainer">
              <Button
                type="button"
                title="Cancel"
                testId="cancelLeadTenant"
                className="leadTenantForm__button"
                plain
                onClick={onCancel}
              />
              <Button
                type="submit"
                title="Save"
                testId="saveLeadTenant"
                className="leadTenantForm__button"
                primary
                loading={isSaving}
              />
            </div>
          </>
        ) : (
          <>
            <div className="leadTenantForm">
              {displayLeadTenantFields.map((field) => (
                <React.Fragment key={field.id}>
                  {(field.value || field.id === 'phoneNumber') && (
                    <SavedInput
                      id={field.id}
                      label={field.label}
                      value={field.value}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="leadTenantForm__buttonContainer">
              <Button
                type="button"
                title="Edit"
                testId="editLeadTenant"
                className="leadTenantForm__button"
                plain
                onClick={onEdit}
              />
            </div>
          </>
        )}
      </Card>
    </form>
  );
};

LeadTenantForm.propTypes = {
  copy: PropTypes.instanceOf(Object).isRequired,
  fields: PropTypes.arrayOf(Object).isRequired,
};

export default LeadTenantForm;
